import { MSAL_ERRORS } from '@/constants/msalConstants'
import { acquireNewToken, getCurrentToken, isTokenExpired, msalLogout } from '@/libs/msalToken'
import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios'
import { API_RETRY_MAX_LIMIT_REACHED } from '@/constants/errors'

// Constants for retry logic
const TIMEOUT_API_RETRY_COUNT = 2
const TOKEN_EXPIRY_API_RETRY_COUNT = 1

export interface ApiError {
  status: number
  message: string
  data?: unknown
  isNetworkError?: boolean
  isAuthError?: boolean
}

export interface ApiResponse<T = unknown> {
  data: T
  status: number
}

// Custom axios request config with retry count
interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retryCount: number
}

const axiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
  },
  timeout: 60000,
})

// Request interceptor
axiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Add CSRF token if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken
      }

      // Get token from storage or refresh if expired
      let token = getCurrentToken('login')

      // If token doesn't exist or is expired, try to acquire a new one
      if (!token || isTokenExpired('login')) {
        try {
          token = await acquireNewToken()
        } catch (error) {
          console.error('Failed to refresh token in request interceptor:', error)
          throw error
        }
      }

      // If we have a token, add it to the request
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      } else {
        throw new Error(MSAL_ERRORS.NO_ACTIVE_ACCOUNT)
      }

      if (import.meta.env.DEV) {
        console.log('🔒 Private API Request:', {
          url: `${config.baseURL}${config.url}`,
          method: config.method?.toUpperCase(),
        })
      }

      return config
    } catch (error) {
      console.error('Request interceptor error:', error)
      return Promise.reject(error)
    }
  },
  (error) => {
    console.error('Request setup error:', error?.message ?? 'Request interceptor error')
    return Promise.reject(error)
  },
)

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => {
    if (import.meta.env.DEV) {
      console.log('✅ Private API Response:', {
        url: response.config.url,
        status: response.status,
      })
    }
    return response
  },
  async (error: AxiosError) => {
    if (import.meta.env.DEV) {
      console.error('❌ Private API Error:', {
        url: error.config?.url,
        status: error.response?.status,
        message: error.message,
      })
    }

    // Ensure originalRequest is properly typed
    const originalRequest = error.config as CustomAxiosRequestConfig

    // Set default retry count if not already set
    if (originalRequest && !originalRequest._retryCount) {
      originalRequest._retryCount = 0
    }

    // Handle timeout/network errors with retry
    if (!error.response && originalRequest && originalRequest._retryCount < TIMEOUT_API_RETRY_COUNT) {
      originalRequest._retryCount++
      console.log(`Retrying request due to network error (attempt ${originalRequest._retryCount})`)
      return axiosInstance(originalRequest)
    }

    // Handle 401 unauthorized errors with token refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      originalRequest._retryCount < TOKEN_EXPIRY_API_RETRY_COUNT
    ) {
      originalRequest._retryCount++
      console.log(`Attempting token refresh (attempt ${originalRequest._retryCount})`)

      try {
        const newToken = await acquireNewToken()
        if (newToken) {
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${newToken}`
          } else {
            originalRequest.headers = {
              Authorization: `Bearer ${newToken}`,
            } as unknown as typeof originalRequest.headers
          }
          return axiosInstance(originalRequest)
        } else {
          throw new Error('Token refresh returned null')
        }
      } catch (refreshError) {
        console.error('Token refresh failed, logging out:', refreshError)
        await msalLogout()

        const apiError: ApiError = {
          status: error.response?.status || 0,
          message: 'Authentication failed. Please log in again.',
          data: error.response?.data,
          isNetworkError: false,
          isAuthError: true,
        }

        return Promise.reject(apiError)
      }
    }

    // If we've already retried for 401 and still getting 401, logout
    if (
      error.response?.status === 401 &&
      originalRequest &&
      originalRequest._retryCount >= TOKEN_EXPIRY_API_RETRY_COUNT
    ) {
      console.log(API_RETRY_MAX_LIMIT_REACHED)
      await msalLogout()

      const apiError: ApiError = {
        status: error.response?.status || 0,
        message: 'Authentication failed. Please log in again.',
        data: error.response?.data,
        isNetworkError: false,
        isAuthError: true,
      }

      return Promise.reject(apiError)
    }

    // For all other errors, format and return
    const apiError: ApiError = {
      status: error.response?.status || 0,
      message: (error.response?.data as { message?: string })?.message || error.message || 'An error occurred',
      data: error.response?.data,
      isNetworkError: !error.response,
      isAuthError: error.response?.status === 401,
    }

    return Promise.reject(apiError)
  },
)

export default axiosInstance
